// Modern Mobile-First Header Component

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, 
  X, 
  User, 
  LogOut, 
  Settings, 
  Bell,
  Search,
  MapPin,
  Calendar,
  FileText,
  Users,
  Home,
  Info,
  BookOpen,
  MessageCircle,
  Phone,
  ChevronDown
} from 'lucide-react';
import WavingIndianFlag from '../ui/WavingIndianFlag';
import Button from '../ui/Button';
import { useAuth } from '../../hooks/useAuth';

const MobileHeader: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    { name: 'Home', href: '/', icon: Home, badge: null },
    { name: 'Mission', href: '/mission', icon: Info, badge: null },
    { name: 'How It Works', href: '/how-it-works', icon: BookOpen, badge: null },
    { name: 'Chapters', href: '/chapters', icon: MapPin, badge: null },
    { name: 'Events', href: '/events', icon: Calendar, badge: null },
    { name: 'Reports', href: '/reports', icon: FileText, badge: null },
    { name: 'Community', href: '/community', icon: MessageCircle, badge: null },
    { name: 'Contact', href: '/contact', icon: Phone, badge: null },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      setIsUserMenuOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsUserMenuOpen(false);
  };

  return (
    <>
      {/* Main Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200/50' 
            : 'bg-white shadow-sm border-b border-gray-200'
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            
                {/* Brand Logo and Text */}
                <Link 
                  to="/" 
                  className="flex items-center group flex-shrink-0"
                  onClick={closeMenu}
                >
                  {/* Logo */}
                  <img 
                    src="/civic-voice-logo.png" 
                    alt="Civic Voice of India" 
                    className="w-8 h-8 sm:w-10 sm:h-10 mr-2 sm:mr-3"
                  />
                  <div className="hidden sm:flex flex-col">
                    <span className="text-base sm:text-lg font-bold text-gradient-flag leading-tight">
                      Civic Voice of India
                    </span>
                    <span className="text-xs text-gray-500 -mt-0.5 hidden lg:block">
                      Transparency. Accountability. Awareness.
                    </span>
                  </div>
                  <div className="sm:hidden">
                    <span className="text-sm font-bold text-gradient-flag leading-tight">
                      Civic Voice of India
                    </span>
                  </div>
                </Link>

            {/* Desktop Navigation - Hidden on mobile */}
            <nav className="hidden xl:flex items-center space-x-1 flex-1 justify-center max-w-3xl">
              {navigationItems.slice(0, 6).map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`relative px-2 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-1 group whitespace-nowrap ${
                      isActivePath(item.href)
                        ? 'bg-primary-50 text-primary-700 shadow-sm'
                        : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-3 h-3" />
                    <span className="text-xs">{item.name}</span>
                    {item.badge && (
                      <span className="absolute -top-1 -right-1 bg-primary-500 text-white text-xs px-1 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Right side actions */}
            <div className="flex items-center space-x-1 sm:space-x-2">
              
              {/* Search - Hidden on small screens */}
              <button className="hidden lg:flex p-1.5 text-gray-400 hover:text-gray-600 transition-colors duration-200 rounded-lg hover:bg-gray-100">
                <Search className="w-4 h-4" />
              </button>

              {/* Auth Section */}
              {isAuthenticated && user ? (
                <div className="flex items-center space-x-2">
                  {/* Notifications */}
                  <button className="relative p-1.5 text-gray-400 hover:text-gray-600 transition-colors duration-200 rounded-lg hover:bg-gray-100">
                    <Bell className="w-4 h-4" />
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full text-xs flex items-center justify-center text-white">
                      3
                    </span>
                  </button>

                  {/* User Menu */}
                  <div className="relative">
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className="flex items-center space-x-1 p-1 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                    >
                      {user.avatar_url ? (
                        <img
                          className="w-7 h-7 rounded-full object-cover ring-2 ring-primary-100"
                          src={user.avatar_url}
                          alt={user.full_name}
                        />
                      ) : (
                        <div className="w-7 h-7 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center">
                          <User className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform duration-200 hidden sm:block ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* User Dropdown */}
                    <AnimatePresence>
                      {isUserMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: -10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: -10 }}
                          transition={{ duration: 0.15 }}
                          className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50"
                        >
                          <div className="px-4 py-3 border-b border-gray-100">
                            <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
                            <p className="text-sm text-gray-500 truncate">{user.email}</p>
                          </div>
                          
                          <Link
                            to="/dashboard"
                            className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                            onClick={closeMenu}
                          >
                            <User className="w-4 h-4" />
                            <span>Dashboard</span>
                          </Link>
                          
                          <Link
                            to="/profile"
                            className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                            onClick={closeMenu}
                          >
                            <Settings className="w-4 h-4" />
                            <span>Settings</span>
                          </Link>
                          
                          <hr className="my-2" />
                          
                          <button
                            onClick={handleLogout}
                            className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                          >
                            <LogOut className="w-4 h-4" />
                            <span>Sign out</span>
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
                  ) : (
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <Link to="/login">
                        <Button variant="ghost" size="sm" className="text-gray-700 text-xs px-2 py-1 font-semibold">
                          <span className="hidden sm:inline">Sign in</span>
                          <span className="sm:hidden">Login</span>
                        </Button>
                      </Link>
                    </div>
                  )}

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="xl:hidden p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              >
                <motion.div
                  animate={{ rotate: isMenuOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {isMenuOpen ? (
                    <X className="w-5 h-5" />
                  ) : (
                    <Menu className="w-5 h-5" />
                  )}
                </motion.div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/20 backdrop-blur-sm"
              onClick={closeMenu}
            />
            
            {/* Menu Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="absolute right-0 top-0 h-full w-80 max-w-sm bg-white shadow-xl"
            >
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-gray-900">Menu</span>
                  </div>
                  <button
                    onClick={closeMenu}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Navigation Links */}
                <nav className="flex-1 px-4 py-6 space-y-2">
                  {navigationItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-base font-medium transition-colors duration-200 ${
                          isActivePath(item.href)
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                        }`}
                        onClick={closeMenu}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.name}</span>
                        {item.badge && (
                          <span className="ml-auto bg-primary-500 text-white text-xs px-2 py-1 rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </nav>

                    {/* Mobile Auth Section */}
                    {!isAuthenticated && (
                      <div className="p-4 border-t border-gray-200">
                        <div className="space-y-3">
                          <Link
                            to="/login"
                            className="block w-full"
                            onClick={closeMenu}
                          >
                            <Button variant="primary" className="w-full justify-center">
                              Admin Login
                            </Button>
                          </Link>
                        </div>
                      </div>
                    )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Spacer for fixed header */}
      <div className="h-14 sm:h-16" />
    </>
  );
};

export default MobileHeader;
